/**
 * Pocket ID sign-in.
 *
 * Backstage's generic `oidc` auth strategy has no built-in frontend auth API ref,
 * so we create one and wire it up with ApiBlueprint + SignInPageBlueprint.
 *
 * `provider.id` MUST stay 'oidc' — it addresses `auth.providers.oidc` on the
 * backend. Only the branding is Pocket ID specific.
 */
import {
  OpenIdConnectApi,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { SignInPage } from '@backstage/core-components';
import {
  createApiRef,
  createFrontendModule,
  configApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
  ApiBlueprint,
} from '@backstage/frontend-plugin-api';

export const pocketIdAuthApiRef = createApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>().with({ id: 'auth.pocketid' });

const pocketIdAuthApi = ApiBlueprint.make({
  name: 'pocketid',
  params: defineParams =>
    defineParams({
      api: pocketIdAuthApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        oauthRequestApi: oauthRequestApiRef,
        configApi: configApiRef,
      },
      factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
        OAuth2.create({
          configApi,
          discoveryApi,
          oauthRequestApi,
          environment: configApi.getOptionalString('auth.environment'),
          provider: {
            id: 'oidc',
            title: 'Pocket ID',
            icon: () => null,
          },
          defaultScopes: ['openid', 'profile', 'email', 'groups'],
        }),
    }),
});

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props => (
      <SignInPage
        {...props}
        provider={{
          id: 'pocketid-auth-provider',
          title: 'Pocket ID',
          message: 'Sign in with your Pocket ID passkey',
          apiRef: pocketIdAuthApiRef,
        }}
      />
    ),
  },
});

export const authModule = createFrontendModule({
  pluginId: 'app',
  extensions: [pocketIdAuthApi, signInPage],
});
